/**
 * LERA Device Engine — Catalog Provider
 * Reads and normalizes the full published Shopify catalog once.
 * No business logic. No brand detection. No alias resolution.
 */
(function (global) {
  'use strict';

  var LERA = (global.LERA = global.LERA || {});
  LERA._modules = LERA._modules || {};

  var debug = LERA._debug;
  var CACHE_PREFIX = 'lera-catalog:global:';
  var GLOBAL_COLLECTION_HANDLE = 'all';
  var PAGE_LIMIT = 250;
  var MAX_PAGES = 24;
  var SCOPE = 'Provider';

  function createStructuredError(code, message, details) {
    return {
      success: false,
      error: {
        code: code,
        message: message,
        details: details || null,
      },
      products: [],
      variants: [],
      meta: {
        fromCache: false,
        productCount: 0,
        variantCount: 0,
        source: 'global',
      },
    };
  }

  function createSuccessPayload(products, meta) {
    var variants = [];

    products.forEach(function (product) {
      (product.variants || []).forEach(function (variant) {
        variants.push(variant);
      });
    });

    return {
      success: true,
      error: null,
      products: products,
      variants: variants,
      meta: meta || {
        fromCache: false,
        productCount: products.length,
        variantCount: variants.length,
        source: 'global',
      },
    };
  }

  function normalizeProduct(product) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      vendor: product.vendor || '',
      productType: product.product_type || '',
      tags: Array.isArray(product.tags)
        ? product.tags
        : String(product.tags || '')
            .split(',')
            .map(function (tag) {
              return tag.trim();
            })
            .filter(Boolean),
      options: product.options || [],
      variants: (product.variants || []).map(function (variant) {
        return {
          id: variant.id,
          title: variant.title,
          option1: variant.option1 || '',
          option2: variant.option2 || '',
          option3: variant.option3 || '',
          available: variant.available,
          sku: variant.sku || '',
        };
      }),
    };
  }

  function buildCacheKey(optionName) {
    return (
      CACHE_PREFIX +
      String(optionName || 'default')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
    );
  }

  function buildPageUrl(page) {
    return (
      '/collections/' +
      GLOBAL_COLLECTION_HANDLE +
      '/products.json?limit=' +
      PAGE_LIMIT +
      '&page=' +
      page
    );
  }

  function readCache(cacheKey) {
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.expiresAt || parsed.expiresAt < Date.now()) {
        sessionStorage.removeItem(cacheKey);
        if (debug && debug.isEnabled()) {
          debug.warn(SCOPE, 'Cache expired — will refetch', { cacheKey: cacheKey });
        }
        return null;
      }

      return parsed;
    } catch (error) {
      if (debug && debug.isEnabled()) {
        debug.error(SCOPE, 'Cache read failed', error, { cacheKey: cacheKey });
      }
      return null;
    }
  }

  function writeCache(cacheKey, payload, ttlMs) {
    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          products: payload.products,
          expiresAt: Date.now() + ttlMs,
          cachedAt: Date.now(),
        })
      );
      if (debug && debug.isEnabled()) {
        debug.log(SCOPE, 'Cache written', {
          cacheKey: cacheKey,
          productCount: payload.products.length,
          ttlMs: ttlMs,
        });
      }
    } catch (error) {
      if (debug && debug.isEnabled()) {
        debug.error(SCOPE, 'Cache write failed', error, { cacheKey: cacheKey });
      }
    }
  }

  function fetchCatalogPage(page) {
    var url = buildPageUrl(page);

    if (debug && debug.isEnabled()) {
      debug.log(SCOPE, 'Fetching catalog page', { url: url, page: page });
    }

    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (debug && debug.isEnabled()) {
          debug.log(SCOPE, 'HTTP response received', {
            url: url,
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
          });
        }

        if (!response.ok) {
          var httpError = createStructuredError('provider_http_error', 'Global catalog request failed.', {
            status: response.status,
            statusText: response.statusText,
            url: url,
            page: page,
            source: 'global',
          });

          if (debug && debug.isEnabled()) {
            debug.error(SCOPE, 'HTTP request failed', httpError.error.message, httpError.error.details);
          }

          return httpError;
        }

        return response.json().then(function (data) {
          var products = ((data && data.products) || []).map(normalizeProduct);
          var variantCount = products.reduce(function (count, product) {
            return count + (product.variants ? product.variants.length : 0);
          }, 0);

          if (debug && debug.isEnabled()) {
            debug.log(SCOPE, 'Page parsed successfully', {
              url: url,
              page: page,
              productCount: products.length,
              variantCount: variantCount,
            });
          }

          return {
            success: true,
            products: products,
          };
        });
      })
      .catch(function (error) {
        if (debug && debug.isEnabled()) {
          debug.error(SCOPE, 'Fetch exception on catalog page', error, { url: url, page: page });
        }

        return createStructuredError('provider_fetch_exception', 'Global catalog fetch threw an exception.', {
          url: url,
          page: page,
          message: error && error.message ? error.message : String(error),
        });
      });
  }

  function fetchAllPublishedProducts() {
    var products = [];
    var page = 1;

    if (debug && debug.isEnabled()) {
      debug.log(SCOPE, 'Starting paginated global catalog fetch', {
        pageLimit: PAGE_LIMIT,
        maxPages: MAX_PAGES,
      });
    }

    function loadNext() {
      if (page > MAX_PAGES) {
        var truncatedPayload = createSuccessPayload(products, { source: 'global', truncated: true });

        if (debug && debug.isEnabled()) {
          debug.warn(SCOPE, 'Pagination limit reached — catalog truncated', {
            totalProducts: truncatedPayload.meta.productCount,
            totalVariants: truncatedPayload.meta.variantCount,
            maxPages: MAX_PAGES,
          });
        }

        return truncatedPayload;
      }

      return fetchCatalogPage(page).then(function (result) {
        if (!result.success) {
          if (debug && debug.isEnabled()) {
            debug.error(SCOPE, 'Pagination stopped due to error', result.error && result.error.message, result.error);
          }
          return result.error ? result : createStructuredError('provider_fetch_failed', 'Global catalog fetch failed.');
        }

        var batch = result.products || [];
        products = products.concat(batch);

        if (debug && debug.isEnabled()) {
          debug.log(SCOPE, 'Pagination progress', {
            page: page,
            batchProductCount: batch.length,
            accumulatedProductCount: products.length,
          });
        }

        if (batch.length < PAGE_LIMIT) {
          var finalPayload = createSuccessPayload(products, { source: 'global', truncated: false });

          if (debug && debug.isEnabled()) {
            debug.log(SCOPE, 'Global catalog fetch complete', {
              totalProducts: finalPayload.meta.productCount,
              totalVariants: finalPayload.meta.variantCount,
              pagesFetched: page,
            });
          }

          return finalPayload;
        }

        page += 1;
        return loadNext();
      });
    }

    return loadNext().catch(function (error) {
      if (debug && debug.isEnabled()) {
        debug.error(SCOPE, 'Unhandled exception in fetchAllPublishedProducts', error);
      }

      return createStructuredError('provider_unhandled_exception', 'Unhandled provider exception.', {
        message: error && error.message ? error.message : String(error),
      });
    });
  }

  var CatalogProvider = {
    CACHE_PREFIX: CACHE_PREFIX,
    GLOBAL_SOURCE: 'global',

    fetchGlobalCatalog: function (options) {
      var optionName = (options && options.optionName) || 'default';
      var cacheEnabled = !options || options.cacheEnabled !== false;
      var cacheTtlMs =
        options && typeof options.cacheTtlMs === 'number' && options.cacheTtlMs > 0
          ? options.cacheTtlMs
          : 30 * 60 * 1000;
      var cacheKey = buildCacheKey(optionName);

      if (debug && debug.isEnabled()) {
        debug.log(SCOPE, 'fetchGlobalCatalog started', {
          optionName: optionName,
          cacheEnabled: cacheEnabled,
          cacheTtlMs: cacheTtlMs,
          cacheKey: cacheKey,
        });
      }

      if (cacheEnabled) {
        var cached = readCache(cacheKey);
        if (cached && cached.products) {
          var cachedPayload = createSuccessPayload(cached.products, {
            fromCache: true,
            productCount: cached.products.length,
            variantCount: cached.products.reduce(function (count, product) {
              return count + (product.variants ? product.variants.length : 0);
            }, 0),
            cachedAt: cached.cachedAt || null,
            expiresAt: cached.expiresAt || null,
            source: 'global',
          });

          if (debug && debug.isEnabled()) {
            debug.log(SCOPE, 'Serving catalog from cache', {
              productCount: cachedPayload.meta.productCount,
              variantCount: cachedPayload.meta.variantCount,
              cachedAt: cached.cachedAt,
              expiresAt: cached.expiresAt,
            });
          }

          return Promise.resolve(cachedPayload);
        }

        if (debug && debug.isEnabled()) {
          debug.log(SCOPE, 'No valid cache — fetching from Shopify');
        }
      }

      return fetchAllPublishedProducts()
        .then(function (result) {
          if (!result.success) {
            if (debug && debug.isEnabled()) {
              debug.error(SCOPE, 'fetchGlobalCatalog failed', result.error && result.error.message, result.error);
            }
            return result;
          }

          if (result.success && cacheEnabled) {
            writeCache(cacheKey, result, cacheTtlMs);
          }

          if (result.meta) {
            result.meta.source = 'global';
          }

          if (debug && debug.isEnabled()) {
            debug.log(SCOPE, 'fetchGlobalCatalog finished successfully', {
              productCount: result.meta.productCount,
              variantCount: result.meta.variantCount,
              fromCache: result.meta.fromCache || false,
            });
          }

          return result;
        })
        .catch(function (error) {
          if (debug && debug.isEnabled()) {
            debug.error(SCOPE, 'fetchGlobalCatalog exception', error);
          }

          return createStructuredError('provider_fetch_global_exception', 'fetchGlobalCatalog threw an exception.', {
            message: error && error.message ? error.message : String(error),
          });
        });
    },

    clearCache: function (optionName) {
      try {
        if (optionName) {
          sessionStorage.removeItem(buildCacheKey(optionName));
          if (debug && debug.isEnabled()) {
            debug.log(SCOPE, 'Cache cleared for option', { optionName: optionName });
          }
          return;
        }

        for (var index = sessionStorage.length - 1; index >= 0; index -= 1) {
          var key = sessionStorage.key(index);
          if (key && key.indexOf(CACHE_PREFIX) === 0) {
            sessionStorage.removeItem(key);
          }
        }

        if (debug && debug.isEnabled()) {
          debug.log(SCOPE, 'All provider caches cleared');
        }
      } catch (error) {
        if (debug && debug.isEnabled()) {
          debug.error(SCOPE, 'clearCache exception', error);
        }
      }
    },
  };

  LERA._modules.catalogProvider = CatalogProvider;
})(typeof window !== 'undefined' ? window : this);
